import clsx from 'clsx';
import { CSSProperties } from 'react';
import * as React from 'react';

import styles from './Button.module.scss';
import { Box, CircularProgress } from '@mui/material';

export enum ButtonWidth {
  Content = 'content',
  Fullwidth = 'fullwidth',
}

export enum ButtonColor {
  Primary = 'primary',
  Secondary = 'secondary',
  SecondaryDark = 'secondaryDark',
  External = 'external',
  Warning = 'warning',
}

export type ButtonProps = {
  id?: string;
  type?: 'submit' | 'button';
  title?: string;
  color?: ButtonColor;
  width?: ButtonWidth;
  className?: string;
  disabled?: boolean;
  icon?: string;
  iconClassName?: string;
  loading?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  style?: CSSProperties;
  testId?: string;
  children?: React.ReactNode;
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'autoFocus' | 'style'>;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      title,
      color,
      width,
      className,
      disabled,
      icon,
      iconClassName,
      loading,
      children,
      testId,
      ...rest
    } = {
      ...DefaultProps,
      ...props,
    };

    const buttonStyles = [
      className,
      styles.button,
      styles[`${width}-button`],
      styles[`${color}-button`],
    ];
    return (
      <button
        ref={ref}
        type={props.type ?? 'button'}
        disabled={disabled}
        {...rest}
        className={clsx(buttonStyles)}
        data-test-id={testId}
      >
        {children ? (
          <div className={styles.rowContainer}>
            <div className={clsx(styles.image, props.iconClassName)}>
              {props.children}
            </div>
            {title}
          </div>
        ) : (
          <div className={styles.rowContainer}>
            {icon && <img className={clsx(iconClassName)} src={icon} />}
            {title}
          </div>
        )}

        {loading && (
          <Box className={styles.loadingContainer}>
            <CircularProgress
              variant="determinate"
              className={styles.loaderOuter}
              size={20}
              thickness={4}
              value={100}
            />
            <CircularProgress
              variant="indeterminate"
              disableShrink
              className={styles.loaderInner}
              size={20}
              thickness={4}
            />
          </Box>
        )}
      </button>
    );
  },
);

const DefaultProps: Partial<ButtonProps> = {
  color: ButtonColor.Primary,
  width: ButtonWidth.Content,
};
